import { ApiError } from "../api";
import { useT } from "../i18n";

/**
 * MutationError says that a change did not happen.
 *
 * It exists because the opposite kept happening. A `useMutation` whose result
 * is never read for failure is a control that looks like it worked: the voice
 * picker snapped back to the old voice with no explanation, Stop left a render
 * billing, removing a correction left it in force, and reordering chapters did
 * nothing at all for weeks (ADR-0041's session). Every one of those was a
 * server saying no into a component that was not listening.
 *
 * The server's own `detail` is preferred over the fallback. It is written for a
 * person — "this book's characters have not been found yet", "a picture may be
 * at most 12 MB" — and it is the difference between a message that tells you
 * what to do and one that tells you something went wrong.
 */
/**
 * failureReferenceOf is the id to show beside a failure, or undefined.
 *
 * Only a 5xx has one. A refusal already says what to do and what to change, so
 * an id beside it is noise; a 500 says the same sentence every time, and the id
 * is the only thing that tells one from another — and the only thing that finds
 * the server's row for it (ADR-0092).
 */
export function failureReferenceOf(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) return undefined;
  if (error.problem.status < 500) return undefined;
  return error.problem.request_id || undefined;
}

export default function MutationError({
  error,
  fallback,
  testId,
  className = "",
}: {
  /** The mutation's `error`. Nothing renders when it is null. */
  error: unknown;
  /** What to say when the failure carried no problem document of its own. */
  fallback: string;
  testId?: string;
  className?: string;
}) {
  const t = useT();
  if (!error) return null;
  const detail =
    error instanceof ApiError
      ? (error.problem.detail ?? error.problem.title)
      : error instanceof Error
        ? error.message
        : "";
  const reference = failureReferenceOf(error);
  return (
    <p
      role="alert"
      data-testid={testId}
      className={`text-sm text-red-600 dark:text-red-400 ${className}`}
    >
      {detail || fallback || t.common.somethingWrongTryAgain}
      {reference && (
        <>
          {" "}
          <span data-testid="failure-reference" className="opacity-70">
            {t.common.failureReference} <bdi>{reference}</bdi>
          </span>
        </>
      )}
    </p>
  );
}
